import React from 'react';
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import { TableColumn } from 'react-data-table-component';
import CustomMaterialMenu from '../../Component/DataGrid/Plugins/CustomMaterialMenu/CustomMaterialMenu';
import DataGrid from '../../Component/DataGrid/DataGrid';
import '../UserDetails/UserDetails.css';
import { formatLimit } from '../../Component/config';
import AddPromoCode from '../../Component/PromoCode/AddPromoCode';
import PromoCodeServices from '../../Services/PromoCodeService';
import { ShowAlert } from '../../Component/ShowAlert';
import { IPromoCode, PromoCodesState, PromoCodesProps, ICashBackCoupon, SelectedTab } from './InterfacePromo';
import { SyncContext, SyncContextType } from '../../Contexts/BatchSyncContext';
import UserAndPermissionService from '../../Services/UserAndPermissionService';
import AddCashBackCoupon from '../../Component/PromoCode/AddCashBackCoupon';
import moment from 'moment';
import EditPromoCode from '../../Component/PromoCode/EditPromoCodeDetials';
import PromoCodeList from './PromocodeUsersList';
import { Status } from '../../Component/Reusables/Utility';

enum Eligible {
    Yes = "Yes",
    No = "No"
}
// Main class for PromoCodes component
class PromoCodes extends React.Component<PromoCodesProps, PromoCodesState> {
    static contextType = SyncContext;
    context!: SyncContextType;
    // Table columns for DataGrid
    private tableColumns: TableColumn<IPromoCode>[] = [
        {
            name: 'Promo Code',
            selector: row => row.promo_code,
            sortable: true,
            id: "promo_Code",
            grow: 1
        },
        {
            name: 'Eligible for First Transaction',
            selector: row => row.applicableFirstTransaction === 1 ? Eligible.Yes : Eligible.No,
            sortable: true,
            id: "applicableFirstTransaction",
            minWidth: "170px",
            center: true,
            grow: 1,
            cell: (row) => {
                const isActive = row.applicableFirstTransaction === 1;
                return (
                    <span className={`status-pill ${isActive ? 'active' : 'inactive'}`}>
                        {isActive ? Eligible.Yes : Eligible.No}
                    </span>
                );
            }
        },
        {
            name: 'Used count',
            selector: row => row.usedCount || '-',
            id: 'usedCount',
            sortable: true,
            center: true,
            grow: 1
        },
        {
            name: 'Amount',
            selector: row => formatLimit(row.amount ?? "", "en-US", "USD", "$"),
            sortable: true,
            id: "amount",
            right: true,
            grow: 1,
            wrap: true,
        },
        {
            name: 'Min Transaction',
            selector: row => formatLimit(row.minTransactionAmt ?? "", "en-US", "USD", "$"),
            sortable: true,
            id: "minTransactionAmt",
            right: true,
            minWidth: "150px",
            grow: 1,
            wrap: true,
        },
        {
            name: 'Influencer',
            selector: row => row.influence_name,
            sortable: true,
            minWidth: "150px",
            id: "influence_name",
            center: true,
            grow: 1,
            wrap: true
        },
        {
            name: 'Status',
            selector: row => row.is_active === 1 ? Status.ACTIVE : Status.EXPIRED,
            sortable: true,
            id: "status",
            grow: 1,
            center: true,
            cell: (row) => {
                return row.is_active === 1
                    ? <span className='status-label active'>{Status.ACTIVE}</span>
                    : <span className="status-label blocked">{Status.EXPIRED}</span>;
            }
        },
        {
            name: 'Valid From',
            selector: row => moment(row.startTime).format('MM-DD-YYYY hh:mm A'),
            sortable: true,
            id: "startTime",
            center: true,
            grow: 2
        },
        {
            name: 'Valid Till',
            selector: row => moment(row.endTime).format('MM-DD-YYYY hh:mm A'),
            sortable: true,
            id: "endTime",
            center: true,
            grow: 2
        },
        {
            name: 'Actions',
            cell: row => {
                const { promoCode } = this.state;
                const menuItems = [
                    ...(promoCode.isVisible ? [{ key: "view_details", label: "View Details" }] : []),
                    ...(row.is_active === 0 || row.is_active === 1
                        ? [{ key: "editpromo", label: "Edit Promo Code" }]
                        : []),
                    ...(promoCode.isBlockCodeVisible && row.is_active === 1
                        ? [{ key: "blocked", label: "Block Code" }]
                        : []),
                    ...(promoCode.isActiveCodeVisible && row.is_active === 0
                        ? [{ key: "activated", label: "Activate Code" }]
                        : []),
                ];

                if (menuItems.length === 0) return null;

                return (
                    <CustomMaterialMenu
                        fontSize="small"
                        multiPopoverItems={menuItems}
                        onPopoverClick={(key_: string) => {
                            if (key_ === "blocked") {
                                this.UpdatePromoCodeStatus(row, key_, 0);
                            } else if (key_ === "activated") {
                                this.UpdatePromoCodeStatus(row, key_, 1);
                            } else if (key_ === "editpromo") {
                                this.setState({
                                    isOpenEditDetailsDialog: true,
                                    selectedPromoCode: row,
                                });
                            } else if (key_ === "view_details") {
                                this.setState({
                                    isOpenViewDetailsDialog: true,
                                    selectedPromoCode: row,
                                });
                            }
                        }}
                        disabled={false}
                    />
                );
            },
            allowOverflow: true,
            button: true,
            width: '120px' // keeping fixed so buttons don’t stretch
        }
    ];


    private tableColumnsForCashBack: TableColumn<ICashBackCoupon>[] = [
        {
            name: 'Coupon Code',
            selector: row => row.coupon_code,
            sortable: true,
            id: "coupon_code",
            grow: 1,
        },
        {
            name: 'Amount',
            selector: row => formatLimit(row.amount, "en-US", "USD", "$"),
            sortable: true,
            id: "amount",
            right: true,
            grow: 1,
            wrap: true,
        },
        {
            name: 'Min Transaction',
            selector: row => formatLimit(row.minTransactionAmt, "en-US", "USD", "$"),
            sortable: true,
            id: "minTransactionAmt",
            right: true,
            grow: 1,
            wrap: true,
        },
        {
            name: 'Transaction Id',
            sortable: true,
            selector: row => row.stripeTransactionId ? row.stripeTransactionId : '-',
            id: "stripeTransactionId",
            right: true,
            wrap: true,
            grow: 1,
            cell: (row) => {
                return row.stripeTransactionId ? (
                    <a
                        href={`/transactionlogs?id=${row.stripeTransactionId}`}
                        style={{ textDecoration: "none" }}
                    >
                        {row.stripeTransactionId}
                    </a>
                ) : (
                    <div style={{ textAlign: "right", marginRight: "45px" }}>-</div>
                );
            }
        },
        {
            name: 'Status',
            selector: row => row.isActive === 1 && row.stripeTransactionId
                ? Status.REDEEMED : row.isActive === 1 && !row.stripeTransactionId
                    ? Status.ACTIVE
                    : Status.BLOCKED,
            sortable: true,
            id: "isActive",
            center: true,
            cell: (row) => {
                const statusLabel = row.isActive === 1 && row.stripeTransactionId
                    ? <span className='status-label active'>{Status.REDEEMED}</span> : row.isActive === 1 && !row.stripeTransactionId
                        ? <span className='status-label active'>{Status.ACTIVE}</span>
                        : <span className="status-label blocked">{Status.BLOCKED}</span>
                return statusLabel;
            },
            grow: 1
        },
        {
            name: 'Expiration Status',
            selector: row => row.isActive !== 1
                ? Status.BLOCKED : row.isExpired === 1
                    ? Status.EXPIRED
                    : Status.VALID,
            sortable: true,
            id: "isExpired",
            center: true,
            grow: 1,
            cell: (row) => {
                let statusLabel;

                if (row.isActive !== 1) {
                    statusLabel = <span className="status-label blocked">{Status.BLOCKED}</span>;
                } else if (row.isExpired === 1) {
                    statusLabel = <span className="status-label blocked">{Status.EXPIRED}</span>;
                } else {
                    statusLabel = <span className="status-label active">{Status.VALID}</span>;
                }

                return statusLabel;
            },
            minWidth: '120px'
        },
        {
            name: 'Valid From',
            selector: row => moment(row.startTime).format('MM-DD-YYYY hh:mm A'),//hh:mm A on Do MMMM YYYY
            sortable: true,
            id: "startTime",
            grow: 2,
            center: true,

        },
        {
            name: 'Valid Till',
            selector: row => moment(row.endTime).format('MM-DD-YYYY hh:mm A'),//hh:mm A on Do MMMM YYYY
            sortable: true,
            id: "endTime",
            grow: 2,
            center: true,
        },
        // {
        //     name: 'Created Date',
        //     selector: row => moment(row.createdDate).format('MM-DD-YYYY hh:mm A'),//hh:mm A on Do MMMM YYYY
        //     sortable: true,
        //     id: "created_date",
        //     width: "190px",
        //     center: true,
        // },
        {
            name: 'Actions',
            cell: row => {
                const { promoCode } = this.state;

                // If both permissions are false, don't render the menu
                if (row.stripeTransactionId) return null;

                const menuItems = [
                    ...(row.isActive === 1 || row.isActive === 0// need to add permissions for edit promo code
                        ? [{ key: "edit_coupon", label: "Edit Coupon" }]
                        : []),
                    ...(promoCode.isBlockCodeVisible && row.isActive === 1
                        ? [{ key: "blocked", label: "Block Code" }]
                        : []),
                    ...(promoCode.isActiveCodeVisible && row.isActive === 0
                        ? [{ key: "activated", label: "Activate Code" }]
                        : []),
                ];

                if (menuItems.length === 0) return null;

                return (
                    <CustomMaterialMenu
                        fontSize="small"
                        multiPopoverItems={menuItems}
                        onPopoverClick={(key_: string, label_: string) => {
                            if (key_ === "blocked") {
                                this.UpdateCashBackCouponStatus(row, key_, 0);
                            } else if (key_ === "activated") {
                                this.UpdateCashBackCouponStatus(row, key_, 1);
                            } else if (key_ === "edit_coupon") {  // Edit Details
                                this.setState({
                                    isOpenEditDetailsDialog: true,
                                    selectedCouponCode: row,
                                });
                            }
                        }}
                        disabled={false}
                    />
                );
            },
            allowOverflow: true,
            button: true,
            width: '120px'
        }
    ]

    constructor(props_: PromoCodesProps) {
        super(props_);
        //Initialize state
        this.state = {
            accessToken: "",
            promoCodeList: [],
            cashbackCouponList: [],
            currentRowsPerPage: 10,
            currentPage: 1,
            totalRows: 100,
            searchText: "",
            filterValue: "",
            isOpenAddPromoCodeDialog: false,
            isOpenAddCashbackCouponDialog: false,
            selectedKey: "",
            userID: "",
            promoCode: {
                isVisible: true,
                isDownloadVisible: true,
                isAddPromoCodeVisible: true,
                isBlockCodeVisible: true,
                isActiveCodeVisible: true
            },
            selectedTab: SelectedTab.PROMO_CODE,
            paginationReset: false,
            isOpenEditDetailsDialog: false,
            selectedPromoCode: {} as IPromoCode,
            selectedCouponCode: {} as ICashBackCoupon,
            isOpenViewDetailsDialog: false
        }
    }

    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            const profile_ = await loadFromLocalStorage('profile');

            if (!accessToken_ || !profile_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            const accessToken = accessToken_.toString();
            const userProfile = typeof profile_ === 'string' ? JSON.parse(profile_) : profile_;

            this.setState({ accessToken }, async () => {
                try {
                    if (userProfile?.roleId) {
                        const permissionResponseRaw = await UserAndPermissionService.getPermissions(accessToken, userProfile?.roleId);
                        const permissionResponse = await permissionResponseRaw.json();

                        if (!permissionResponse?.permissions?.promoCode?.isVisible) {
                            console.warn("Permission denied, logging out...");
                            localStorage.clear();
                            window.location.href = '/';
                            return;
                        }

                        const permissions = permissionResponse.permissions

                        this.context.dispatch({
                            type: "SET_PERMISSION", payload: {
                                permissions
                            },
                        });

                        const promoCode = permissionResponse.permissions.promoCode

                        this.setState({ promoCode }, () => {
                            this.fetchData()
                        });

                    } else {
                        this.fetchData()
                    }
                } catch (error) {
                    console.error("Error fetching permissions:", error);
                }
            });
        } catch (error) {
            console.error("Error in componentDidMount:", error);
            localStorage.clear();
            window.location.href = '/';
        }
    }

    handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        this.setState(prevState => ({
            selectedTab: newValue,
            currentPage: 1,
            paginationReset: !prevState.paginationReset,
        }), () => this.fetchData());
    };

    fetchData = () => {
        switch (this.state.selectedTab) {
            case SelectedTab.PROMO_CODE:
                this.getAllPromoCodes();
                break;
            case SelectedTab.CASHBACK_COUPON:
                this.getAllCashBackCoupons();
                break;
            default:
                break;
        }
    };

    getTableData = () => {
        switch (this.state.selectedTab) {
            case SelectedTab.PROMO_CODE:
                return this.state.promoCodeList;
            case SelectedTab.CASHBACK_COUPON:
                return this.state.cashbackCouponList;
            default:
                return [];
        }
    };

    getTableColumns(): TableColumn<IPromoCode | ICashBackCoupon>[] {
        switch (this.state.selectedTab) {
            case SelectedTab.PROMO_CODE:
                return this.tableColumns as TableColumn<IPromoCode | ICashBackCoupon>[];
            case SelectedTab.CASHBACK_COUPON:
                return this.tableColumnsForCashBack as TableColumn<IPromoCode | ICashBackCoupon>[];
            default:
                return [];
        }
    }

    private handlePromotionalCode = () => {
        switch (this.state.selectedTab) {
            case SelectedTab.PROMO_CODE:
                this.setState({ isOpenAddPromoCodeDialog: true });
                break;
            case SelectedTab.CASHBACK_COUPON:
                this.setState({ isOpenAddCashbackCouponDialog: true });
                break;
            default:
                console.warn("Unhandled tab selection");
                break;
        }
    };

    private getSearchPlaceholder() {
        return this.state.selectedTab === SelectedTab.PROMO_CODE ? 'promo code' : "cashback coupon";
    }

    public render() {
        const { promoCode, searchText, selectedTab, totalRows, paginationReset } = this.state;
        return (
            <>
                <div className="body-sub-container table-container">
                    <DataGrid
                        title='Promotionals'
                        search={this.getSearchPlaceholder()}
                        isPromoCodeBtn={this.handlePromotionalCode}
                        defaultSearch={searchText}
                        onSearch={(searchText_: string) => {
                            this.setState({
                                searchText: searchText_,
                                currentPage: 1
                            }, () => {
                                this.fetchData();
                            });
                        }}
                        columns={this.getTableColumns()}
                        data={this.getTableData()}
                        onChangePage={(page: number, totalRows: number) => {
                            this.setState({
                                currentPage: page
                            }, () => {
                                this.fetchData();
                            })
                        }}
                        onChangeRowsPerPage={(currentRowsPerPage_: number, currentPage_: number) => {
                            this.setState({
                                currentRowsPerPage: currentRowsPerPage_,
                                currentPage: currentPage_
                            }, () => {
                                this.fetchData();
                            })
                        }}
                        pagination
                        paginationTotalRows={totalRows}
                        exportCsv={promoCode.isDownloadVisible ? true : false}
                        csvFileName={`Promotionals - ${this.getSearchPlaceholder()}`}
                        isPromoCodeAddVisible={promoCode.isAddPromoCodeVisible ? true : false}
                        onTabChange={this.handleTabChange}
                        selectedTab={selectedTab}
                        isPromotionalPage={true}
                        paginationDefaultPage={1}
                        paginationResetDefaultPage={paginationReset}
                    />
                </div>
                {this.state.isOpenAddPromoCodeDialog &&
                    <AddPromoCode
                        open={this.state.isOpenAddPromoCodeDialog}
                        onClose={(state: boolean, action: string) => {
                            this.setState({
                                isOpenAddPromoCodeDialog: false
                            })
                        }}
                        accessToken={this.state.accessToken}
                        refreshData={() => this.getAllPromoCodes()}
                    />
                }
                {this.state.isOpenAddCashbackCouponDialog &&
                    <AddCashBackCoupon
                        open={this.state.isOpenAddCashbackCouponDialog}
                        onClose={(state: boolean, action: string) => {
                            this.setState({
                                isOpenAddCashbackCouponDialog: false
                            })
                        }}
                        accessToken={this.state.accessToken}
                        refreshData={() => this.getAllCashBackCoupons()}
                    />
                }
                {
                    this.state.isOpenEditDetailsDialog && (this.state.selectedPromoCode || this.state.selectedCouponCode) && (
                        <EditPromoCode
                            open={this.state.isOpenEditDetailsDialog}
                            onClose={(state: boolean, action: string) => {
                                this.setState({
                                    isOpenEditDetailsDialog: false
                                })
                            }}
                            selectedPromoData={selectedTab === SelectedTab.PROMO_CODE ? this.state.selectedPromoCode : this.state.selectedCouponCode}
                            accessToken={this.state.accessToken}
                            currentTab={selectedTab === SelectedTab.PROMO_CODE ? 'promo_code' : "cashback_coupon"}
                            refreshData={() => selectedTab === SelectedTab.PROMO_CODE ? this.getAllPromoCodes() : this.getAllCashBackCoupons()}
                        />
                    )
                }
                {
                    this.state.isOpenViewDetailsDialog && this.state.selectedPromoCode && (
                        <PromoCodeList
                            open={this.state.isOpenViewDetailsDialog}
                            onClose={(state: boolean, action: string) => {
                                this.setState({
                                    isOpenViewDetailsDialog: false
                                })
                            }}
                            selectedPromoData={selectedTab === SelectedTab.PROMO_CODE ? this.state.selectedPromoCode : this.state.selectedCouponCode}
                            accessToken={this.state.accessToken}
                            // currentTab={selectedTab === SelectedTab.PROMO_CODE ? 'promo_code' : "cashback_coupon"}
                            refreshData={() => selectedTab === SelectedTab.PROMO_CODE ? this.getAllPromoCodes() : this.getAllCashBackCoupons()}
                        />
                    )
                }
                {
                    this.state.isOpenViewDetailsDialog && this.state.selectedPromoCode && (
                        <PromoCodeList
                            open={this.state.isOpenViewDetailsDialog}
                            onClose={(state: boolean, action: string) => {
                                this.setState({
                                    isOpenViewDetailsDialog: false
                                })
                            }}
                            selectedPromoData={selectedTab === SelectedTab.PROMO_CODE ? this.state.selectedPromoCode : this.state.selectedCouponCode}
                            accessToken={this.state.accessToken}
                            // currentTab={selectedTab === SelectedTab.PROMO_CODE ? 'promo_code' : "cashback_coupon"}
                            refreshData={() => selectedTab === SelectedTab.PROMO_CODE ? this.getAllPromoCodes() : this.getAllCashBackCoupons()}
                        />
                    )
                }
            </>
        )
    }

    // Fetch all promo codes..
    private async getAllPromoCodes() {
        let pageIndex: number = this.state.currentPage - 1;
        let pagination: any = {
            pageNumber: pageIndex,
            recordPerPage: this.state.currentRowsPerPage
        };

        if (this.state.searchText) {
            pagination["searchText"] = this.state.searchText;
        }

        try {
            const tr = await PromoCodeServices.getAllPromoCode(this.state.accessToken, pagination);

            if (tr.ok) {
                const resp = await tr.json();
                const updatedResp = resp.promoCodeList.map((code: any) => {
                    const { amount, ...addedCurrentSymbol } = code;
                    const updatedAmount = formatLimit(amount, "en-US", "USD", "$");
                    return { ...addedCurrentSymbol, amount: updatedAmount }
                });
                this.setState({
                    promoCodeList: resp.promoCodeList || [],
                    totalRows: resp.pageCount,
                });
            } else {
                ShowAlert("Error", "Failed to fetch promo codes. Please try again later.", "error");
            }
        } catch (error) {
            console.error("Error fetching promo codes:", error);
            ShowAlert("Error", "An unexpected error occurred while fetching promo codes.", "error");
        }
    }

    // Fetch all cashback coupons...
    private async getAllCashBackCoupons() {
        let pageIndex: number = this.state.currentPage - 1;
        let pagination: any = {
            pageNumber: pageIndex,
            recordPerPage: this.state.currentRowsPerPage
        };

        if (this.state.searchText) {
            pagination["searchText"] = this.state.searchText;
        }

        try {
            const tr = await PromoCodeServices.getAllCashBackCoupons(this.state.accessToken, pagination);

            if (tr.ok) {
                const resp = await tr.json();
                this.setState({
                    cashbackCouponList: resp.cashbackCouponList || [],
                    totalRows: resp.pageCount,
                });
            } else {
                ShowAlert("Error", "Failed to fetch cashback coupon. Please try again later.", "error");
            }
        } catch (error) {
            console.error("Error fetching cashback coupon:", error);
            ShowAlert("Error", "An unexpected error occurred while fetching cashback coupon.", "error");
        }
    }

    // Update the status of a promo code, Activate or Block
    private async UpdatePromoCodeStatus(row_: IPromoCode, key_: string, status: number) {
        try {
            const promoCodeGuid = row_.promo_code_id;
            const promoCode = row_.promo_code;

            const resp = await PromoCodeServices.UpdatePromoCodeStatus(this.state.accessToken, promoCodeGuid, status);

            if (resp?.ok) {
                await this.getAllPromoCodes();
                ShowAlert("PassportPay", `Promo code ${promoCode} has been ${key_}.`, "success");
            } else {
                let errorMessage = "Failed to update the promo code status. Please try again later.";
                ShowAlert("Error", errorMessage, "error");
            }
        } catch (error) {
            console.error("Error updating promo code status:", error);
            ShowAlert("Error", "An unexpected error occurred while updating the promo code status. Please try again.", "error");
        }
    }

    // Update the status of a CashBack Coupon Status, Activate or Block
    private async UpdateCashBackCouponStatus(row_: ICashBackCoupon, key_: string, status: number) {
        try {
            const coupon_id = row_.cashbackCouponId;
            const coupon_Code = row_.coupon_code;

            const resp = await PromoCodeServices.UpdateCashBackCouponStatus(this.state.accessToken, coupon_id, status);

            if (resp?.ok) {
                await this.getAllCashBackCoupons();
                ShowAlert("PassportPay", `Cashback coupon ${coupon_Code} has been ${key_}.`, "success");
            } else {
                let errorMessage = "Failed to update the cashback coupon status. Please try again later.";
                ShowAlert("Error", errorMessage, "error");
            }
        } catch (error) {
            console.error("Error updating cashback coupon status:", error);
            ShowAlert("Error", "An unexpected error occurred while updating the cashback coupon. Please try again.", "error");
        }
    }
}

export default PromoCodes;